import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { useMetaRobots } from "@/lib/use-meta-robots";
import { AccountShell } from "@/components/account/AccountShell";
import { CoinAmount } from "@/components/economy/CoinAmount";
import { ACCOUNT_ECONOMY } from "@/constants/testIds";

export const AccountPage = () => {
  const { user, loading, login, loggedOutReason } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  // Login-gated, no unique public content - keep it out of search results.
  useMetaRobots("noindex, nofollow");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const data = new FormData(e.target);
    setSubmitting(true);
    try {
      await login(data.get("username").trim(), data.get("password"));
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        toast("Přihlášení se nezdařilo", {
          description: "Zkontroluj uživatelské jméno a heslo.",
        });
      } else {
        toast("Přihlášení se nezdařilo", {
          description: "Zkus to prosím znovu za chvíli.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section className="min-h-[70vh] px-6 pb-24 pt-40 text-center" data-testid="account-loading">
        <p className="font-display text-sm uppercase tracking-[0.2em] text-white/40">
          Načítám…
        </p>
      </section>
    );
  }

  if (!user) {
    return (
      <section className="min-h-[70vh] px-6 pb-24 pt-40" data-testid="account-login-section">
        <div className="mx-auto w-full max-w-md rounded-xl border border-white/10 bg-[#17171b] p-8 sm:p-10">
          <div className="mb-2 flex items-center gap-2">
            <i className="fa-solid fa-dragon text-[#F0801F]" />
            <span className="font-display text-xs uppercase tracking-[0.28em] text-[#F0801F]">
              Draconia RP
            </span>
          </div>
          <h1 className="font-display text-2xl font-600 uppercase tracking-tight text-white">
            Přihlas se do účtu
          </h1>
          <p className="mt-2 text-sm text-white/50">
            Použij stejné jméno a heslo jako do hry.
          </p>

          {loggedOutReason === "idle" && (
            <div
              data-testid="session-expired-notice"
              className="mt-5 rounded-md border border-[#F0801F]/40 bg-[#F0801F]/10 px-4 py-3 text-sm text-[#F0801F]"
            >
              Byl jsi odhlášen kvůli delší neaktivitě. Přihlas se prosím znovu.
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-7 space-y-4">
            <div>
              <label
                htmlFor="login-user"
                className="mb-2 block font-display text-[11px] uppercase tracking-[0.16em] text-white/50"
              >
                Uživatelské jméno
              </label>
              <div className="relative">
                <i className="fa-solid fa-user absolute left-4 top-1/2 -translate-y-1/2 text-sm text-white/30" />
                <input
                  id="login-user"
                  name="username"
                  type="text"
                  placeholder="hrdina_faerunu"
                  maxLength={17}
                  required
                  data-testid="login-username"
                  className="w-full rounded-md border border-white/10 bg-white/[0.04] py-3 pl-11 pr-4 text-white outline-none transition-all placeholder:text-white/25 focus:border-[#F0801F]/60 focus:bg-white/[0.06]"
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="login-pass"
                className="mb-2 block font-display text-[11px] uppercase tracking-[0.16em] text-white/50"
              >
                Heslo
              </label>
              <div className="relative">
                <i className="fa-solid fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-sm text-white/30" />
                <input
                  id="login-pass"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  maxLength={16}
                  required
                  data-testid="login-password"
                  className="w-full rounded-md border border-white/10 bg-white/[0.04] py-3 pl-11 pr-4 text-white outline-none transition-all placeholder:text-white/25 focus:border-[#F0801F]/60 focus:bg-white/[0.06]"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="mt-2 w-full rounded-md bg-[#F0801F] py-3.5 font-display text-sm font-600 uppercase tracking-[0.12em] text-[#141417] transition-colors hover:bg-[#ff9438] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "Přihlašuji…" : "Přihlásit se"}
            </button>
          </form>
          <p className="mt-4 text-center text-xs text-white/40">
            Heslo v klientovi hry nerozlišuje velká/malá písmena.
          </p>
        </div>
      </section>
    );
  }

  const charCount = user.characters.length;

  return (
    <AccountShell isGm={user.is_gm} testId="account-dashboard">
      <div className="mb-10">
        <p className="font-display text-xs font-600 uppercase tracking-[0.3em] text-[#F0801F]">
          Tvůj účet
        </p>
        <h1 className="mt-3 font-display text-3xl font-600 uppercase tracking-tight text-white sm:text-4xl">
          Vítej, {user.username}
        </h1>
      </div>

      <div
        className="rounded-xl border border-white/10 bg-[#17171b] p-8"
        data-testid="characters-card"
      >
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-display text-lg font-600 uppercase tracking-tight text-white">
            Tvoje postavy
          </h2>
          <span
            data-testid="characters-count"
            className="rounded-full border border-[#F0801F]/40 px-3 py-1 font-display text-xs font-600 text-[#F0801F]"
          >
            {charCount}
          </span>
        </div>
        {charCount > 0 && (
          <p className="mb-5 text-xs text-white/40">
            Hotovost u sebe se ve hře ukládá jen když postava není online. Pokud jsi právě
            hrál(a), odloguj se ze hry a načti stránku znovu (F5), aby se ti zobrazila
            aktuální částka.
          </p>
        )}
        {charCount === 0 ? (
          <p className="text-sm text-white/50">
            Zatím nemáš žádnou postavu. Připoj se ke hře a vytvoř si první!
          </p>
        ) : (
          <ul className="space-y-2">
            {user.characters.map((c) => (
              <li key={c.guid} className="rounded-md border border-white/10 px-4 py-3">
                <div className="flex items-center gap-3 text-sm text-white/80">
                  <i className="fa-solid fa-dragon text-xs text-[#F0801F]" />
                  {c.name}
                </div>
                <p className="mb-1.5 mt-3 text-[11px] uppercase tracking-[0.14em] text-white/40">
                  Hotovost u sebe
                </p>
                <CoinAmount amount={c.money_gros} data-testid={ACCOUNT_ECONOMY.characterMoney} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </AccountShell>
  );
};
